import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Bugsnag from "@bugsnag/js";
import { DEFAULT_NOTIFICATION_PAYLOAD, NOTIFICATION_VARIANTS } from "@/utils/applicationConstants";
import { ANALYTIC_EVENTS, sendAnalytics } from "@/utils/analytics";
import { baseUrl, onPrepareHeaders } from "./helpers";
import { showNotification } from "../actions/notification";
import { requestCloseModal } from "../actions/modal";
import { lenderCallLogService } from "./lenderCallLogService";
import { loanService } from "./loanService";

export const loanBorrowerService = createApi({
  reducerPath: "loanBorrowerApi",
  tagTypes: ["LoanBorrower"],
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: onPrepareHeaders,
  }),
  endpoints: builder => ({
    getLoanBorrowers: builder.query({
      query: id => `loans/${id}/borrowers`,
      providesTags: result =>
        result
          ? [
              ...result.map(({ borrowerId }) => ({ type: "LoanBorrower", id: borrowerId })),
              { type: "LoanBorrower", id: "LIST" },
            ]
          : [{ type: "LoanBorrower", id: "LIST" }],
    }),
    addLoanBorrower: builder.mutation({
      query: ({ loanId, borrowerId }) => ({
        url: `loans/${loanId}/borrowers/${borrowerId}`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "LoanBorrower", id: "LIST" }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LOAN_BORROWER_ADDED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: `${data.borrowerFullName} has been added to the loan application!`,
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(lenderCallLogService.util.invalidateTags(["LenderCallLog"]));
          dispatch(loanService.util.invalidateTags(["LoanLendersHeatMap"]));
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    updateLoanBorrower: builder.mutation({
      query: ({ loanId, borrowerId, ...body }) => ({
        url: `loans/${loanId}/borrowers/${borrowerId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [{ type: "LoanBorrower", id: "LIST" }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LOAN_BORROWER_UPDATED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: `${data.borrowerFullName} has been updated in the loan application!`,
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(lenderCallLogService.util.invalidateTags(["LenderCallLog"]));
          dispatch(loanService.util.invalidateTags(["LoanLendersHeatMap"]));
          dispatch(requestCloseModal());
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    patchLoanBorrower: builder.mutation({
      query: ({ loanId, borrowerId, ...body }) => ({
        url: `loans/${loanId}/borrowers/${borrowerId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: "LoanBorrower", id: "LIST" }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.BORROWER_NEEDS_LIST_INSTRUCTIONS_UPDATED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: `Instructions for ${data.borrowerFullName} have been updated!`,
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(requestCloseModal());
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    deleteLoanBorrower: builder.mutation({
      query({ loanId, borrowerId }) {
        return {
          url: `loans/${loanId}/borrowers/${borrowerId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: [{ type: "LoanBorrower", id: "LIST" }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LOAN_BORROWER_DELETED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: `${data.borrowerFullName} has been removed from the loan application!`,
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
  }),
});

export const {
  useGetLoanBorrowersQuery,
  useAddLoanBorrowerMutation,
  useUpdateLoanBorrowerMutation,
  usePatchLoanBorrowerMutation,
  useDeleteLoanBorrowerMutation,
} = loanBorrowerService;
